import { useEffect, useRef, useState } from "react";
import { useForm, useWatch } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import {
  useDeletePOMutation,
  useGetPurchaseOrderByIdMutation,
  useGetPurchaseOrders,
} from "../../../../api/services/client/clientApis";
import { useSiteCore } from "../../../../hooks/useSiteCore";
import {
  currencyListSelector,
  siteCoreContentLoadingSelector,
} from "../../../../redux/slices/appState/appStateSelector";
import { createClientSelector } from "../../../../redux/slices/client/createClientSelector";
import {
  PurchaseOrder,
  PurchaseOrderActionType,
} from "../../modelsAndSchemas/Models";
import { purchaseOrdersListSiteCoreKeys } from "../../siteCore/SiteCoreKeys";

import { isString } from "lodash";
import { useGetLoggedInUserDetails } from "../../../../aAdminTabTeam2/hooksAndFunctions/customHooks/useGetLoggedInUserDetails";
import { BE_ERROR_CODES, PAGE_IDS } from "../../../../helpers/constants";
import { getDateFormatByCountry } from "../../../../helpers/constants/countryDateFormat";
import {
  convertToUserFormat,
  getParsedBEErrors,
  getPOStatusFromExpirationDate,
  isEmpty,
  isNotEmpty,
} from "../../../../helpers/utils";
import { useNavigationSiteCore } from "../../../../hooks/useNavigationSiteCore";
import {
  setIsDataModifiedCurrentPage,
  toggleLoader,
  toggleToast,
} from "../../../../redux/slices/appState/appStateSlice";
import { filterPurchaseOrders } from "../../helper";
const { purchaseOrderTitle, statusList, poDeletedMessage } =
  purchaseOrdersListSiteCoreKeys;
export const usePurchaseOrderListHook = () => {
  const pageId = PAGE_IDS.PURCHASE_ORDER_LIST;
  const { control } = useForm<PurchaseOrder>();
  const tableRef = useRef<{ resetPagination: () => void }>(null);
  const {
    fetchLabel,
    commonLabels,
    fetchValueByKeyOfDropdown,
    fetchDropdownValues,
  } = useSiteCore();
  const { commonARIALabel } = useNavigationSiteCore();
  const siteCoreLoading = useSelector(siteCoreContentLoadingSelector);
  const { loggedInUser } = useGetLoggedInUserDetails();
  const userCountryDateFormat = getDateFormatByCountry(
    loggedInUser?.countryCode ?? "",
  );
  const [filteredPoList, setFilteredPoList] = useState<PurchaseOrder[]>([]);
  const [purchaseOrderList, setPurchaseOrderList] = useState<PurchaseOrder[]>(
    [],
  );
  const [showAddPurchaseOrderModal, setShowAddPurchaseOrderModal] =
    useState(false);
  const [showManageTransactionModal, setShowManageTransactionModal] =
    useState(false);
  const [showReOpenPoModal, setShowReOpenPoModal] = useState(false);
  const [purchaseOrder, setPurchaseOrder] = useState<PurchaseOrder | null>(
    null,
  );
  const [showViewPurchaseOrderModal, setShowViewPurchaseOrderModal] =
    useState(false);
  const [showViewPOtransactionsModal, setShowViewPOtransactionsModal] =
    useState(false);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [showDuplicateWarningModal, setShowDuplicateWarningModal] =
    useState(false);

  const currencyList = useSelector(currencyListSelector);

  const isViewPurchaseOrderRef = useRef(false);
  const isEditPurchaseOrderRef = useRef(false);
  const isViewTransactionsRef = useRef(false);
  const isReOpenPurchaseOrderRef = useRef(false);

  const dispatch = useDispatch();
  const {
    mutateAsync: getPurchaseOrderById,
    data: purchaseOrderData,
    isPending: isGetPurchaseOrderPending,
    isSuccess: isGetPurchaseOrderSuccess,
    isError: isGetPurchaseOrderError,
    error: getPurchaseOrderError,
  } = useGetPurchaseOrderByIdMutation();

  const {
    mutateAsync: deletePurchaseOrder,
    isPending: isDeletePurchaseOrderPending,
    isSuccess: isDeletePurchaseOrderSuccess,
    isError: isDeletePurchaseOrderError,
    error: deletePurchaseOrderError,
  } = useDeletePOMutation();

  useEffect(() => {
    dispatch(
      toggleLoader({
        isVisible: isGetPurchaseOrderPending,
        message: commonLabels.loadingPleaseWait,
      }),
    );
    dispatch(
      toggleLoader({
        isVisible: isDeletePurchaseOrderPending,
        message: commonLabels.loadingPleaseWait,
      }),
    );
  }, [isGetPurchaseOrderPending, isDeletePurchaseOrderPending]);

  useEffect(() => {
    if (isGetPurchaseOrderError && getPurchaseOrderError) {
      dispatch(
        toggleToast({
          open: true,
          message: commonLabels.someThingWentWrongPleaseTryAgainLater,
        }),
      );
    }
    if (isDeletePurchaseOrderError && isNotEmpty(deletePurchaseOrderError)) {
      const error = getParsedBEErrors(deletePurchaseOrderError);
      if (
        isNotEmpty(error) &&
        error[0].errorCode ===
          BE_ERROR_CODES.PURCHASE_ORDER_NUMBER_TAGGED_IN_OTHER_PLACE
      ) {
        setShowDuplicateWarningModal(true);
        setShowConfirmationModal(false);
      } else {
        dispatch(
          toggleToast({
            open: true,
            message: error[0]?.message,
          }),
        );
      }
    }
  }, [
    isGetPurchaseOrderError,
    isDeletePurchaseOrderError,
    getPurchaseOrderError,
    deletePurchaseOrderError,
  ]);

  useEffect(() => {
    if (isGetPurchaseOrderSuccess) {
      if (isViewPurchaseOrderRef.current) {
        setShowViewPurchaseOrderModal(true);
      } else if (isViewTransactionsRef.current) {
        setShowViewPOtransactionsModal(true);
      } else if (isReOpenPurchaseOrderRef.current) {
        setShowReOpenPoModal(true);
      } else {
        isEditPurchaseOrderRef.current = true;
        setShowAddPurchaseOrderModal(true);
      }
    }
  }, [isGetPurchaseOrderSuccess]);

  useEffect(() => {
    if (isDeletePurchaseOrderSuccess) {
      setPurchaseOrder(null);
      setShowAddPurchaseOrderModal(false);
      setShowConfirmationModal(false);
      dispatch(
        toggleToast({
          open: true,
          message: getLabel(poDeletedMessage),
        }),
      );
      refetch();
    }
  }, [isDeletePurchaseOrderSuccess]);

  const handleAddPurchaseOrder = () => {
    if (!showAddPurchaseOrderModal) {
      isEditPurchaseOrderRef.current = false;
      setShowAddPurchaseOrderModal(true);
    }
  };

  const handlePurchaseOrderEditFromView = (purchaseOrderId: string) => {
    isEditPurchaseOrderRef.current = true;
    isViewPurchaseOrderRef.current = false;
    isViewTransactionsRef.current = false;
    isReOpenPurchaseOrderRef.current = false;
    setShowViewPurchaseOrderModal(false);
    setShowViewPOtransactionsModal(false);
    getPurchaseOrderById(purchaseOrderId);
  };

  const handleViewPurchaseOrderTransaction = (purchaseOrder: PurchaseOrder) => {
    isEditPurchaseOrderRef.current = false;
    isViewPurchaseOrderRef.current = false;
    isViewTransactionsRef.current = true;
    isReOpenPurchaseOrderRef.current = false;
    getPurchaseOrderById(purchaseOrder.purchaseOrderId);
  };

  const handleReopenPurchaseOrderTransaction = (
    purchaseOrder: PurchaseOrder,
  ) => {
    isEditPurchaseOrderRef.current = false;
    isViewPurchaseOrderRef.current = false;
    isViewTransactionsRef.current = false;
    isReOpenPurchaseOrderRef.current = true;
    getPurchaseOrderById(purchaseOrder.purchaseOrderId);
  };

  const handleEditPurchaseOrder = (purchaseOrder: PurchaseOrder) => {
    isViewTransactionsRef.current = false;
    isViewPurchaseOrderRef.current = false;
    isReOpenPurchaseOrderRef.current = false;
    isEditPurchaseOrderRef.current = true;
    getPurchaseOrderById(purchaseOrder.purchaseOrderId);
  };

  const handleViewPurchaseOrder = (purchaseOrder: PurchaseOrder | string) => {
    isViewPurchaseOrderRef.current = true;
    isViewTransactionsRef.current = false;
    isEditPurchaseOrderRef.current = false;
    isReOpenPurchaseOrderRef.current = false;
    getPurchaseOrderById(
      isString(purchaseOrder) ? purchaseOrder : purchaseOrder?.purchaseOrderId,
    );
  };

  const handlePurchaseOrderAdded = (purchaseOrderId?: string) => {
    refetch();
    if (purchaseOrderId && isNotEmpty(purchaseOrderId)) {
      handleViewPurchaseOrder(purchaseOrderId);
    }
    dispatch(setIsDataModifiedCurrentPage(true));
  };

  const { id: clientId } = useSelector(createClientSelector);
  const {
    data,
    refetchWithParams: refetchPurchaseOrders,
    refetch,
  } = useGetPurchaseOrders();

  const filters = useWatch({ control });

  useEffect(() => {
    if (isEmpty(clientId)) return;
    refetchPurchaseOrders({ clientCode: clientId });
  }, [clientId]);

  useEffect(() => {
    if (data && isNotEmpty(data) && !siteCoreLoading) {
      let isPoListExists = purchaseOrderList.length;
      const mapPurchaseOrderList = mapPurchaseOrders(data);
      setPurchaseOrderList(mapPurchaseOrderList);
      if (isPoListExists) appliedFilters(mapPurchaseOrderList);
      else setFilteredPoList(mapPurchaseOrderList);
    } else {
      setPurchaseOrderList([]);
      setFilteredPoList([]);
    }
  }, [data, siteCoreLoading]);

  useEffect(() => {
    appliedFilters(purchaseOrderList);
  }, [filters]);

  const appliedFilters = (purchaseOrders: PurchaseOrder[]) => {
    if (!purchaseOrders.length) return;
    const filterList = filterPurchaseOrders(
      purchaseOrders,
      filters as PurchaseOrder,
    );
    setFilteredPoList(filterList);
    resetPagination();
  };

  const mapPurchaseOrders = (purchaseOrders: PurchaseOrder[]) => {
    return purchaseOrders.map((order) => {
      order.statusId = getPOStatusFromExpirationDate(
        order.statusId,
        convertToUserFormat(order.expiryDate, userCountryDateFormat),
      );
      order.status = fetchValueByKeyOfDropdown(
        purchaseOrderTitle,
        statusList,
        order.statusId,
      );
      order.effectiveDate = convertToUserFormat(
        order.effectiveDate,
        userCountryDateFormat,
      );
      order.expiryDate = isNotEmpty(order.expiryDate)
        ? convertToUserFormat(order.expiryDate, userCountryDateFormat)
        : "";
      return order;
    });
  };

  const resetPagination = () => {
    if (tableRef.current) {
      tableRef.current.resetPagination();
    }
  };

  const getLabel = (key: string) => {
    return fetchLabel(purchaseOrderTitle, key);
  };

  const handleDelete = () => {
    if (purchaseOrder?.purchaseOrderId) {
      deletePurchaseOrder({
        purchaseOrderId: purchaseOrder?.purchaseOrderId,
      });
    }
    dispatch(setIsDataModifiedCurrentPage(true));
  };

  const handleActionClick = (
    type: PurchaseOrderActionType,
    purchaseOrder: PurchaseOrder,
  ) => {
    switch (type) {
      case "managePoTransactions":
        setShowManageTransactionModal(true);
        setPurchaseOrder(purchaseOrder);
        break;

      case "edit":
        handleEditPurchaseOrder(purchaseOrder);
        break;

      case "viewPoTransactions":
        handleViewPurchaseOrderTransaction(purchaseOrder);
        break;

      case "reOpenPoNumber":
        handleReopenPurchaseOrderTransaction(purchaseOrder);
        break;

      case "delete":
        setShowConfirmationModal(true);
        setPurchaseOrder(purchaseOrder);
        break;

      default:
        break;
    }
  };

  return {
    purchaseOrderList,
    tableRef,
    pageId,
    filteredPoList,
    commonLabels,
    getLabel,
    handleAddPurchaseOrder,
    showAddPurchaseOrderModal,
    purchaseOrder,
    setShowAddPurchaseOrderModal,
    showViewPurchaseOrderModal,
    showViewPOtransactionsModal,
    setShowViewPurchaseOrderModal,
    setShowViewPOtransactionsModal,
    showManageTransactionModal,
    setShowManageTransactionModal,
    showConfirmationModal,
    setShowConfirmationModal,
    showDuplicateWarningModal,
    setShowDuplicateWarningModal,
    refetchPoList: refetch,
    handleActionClick,
    control,
    fetchDropdownValues,
    userCountryDateFormat,
    purchaseOrderData,
    handlePurchaseOrderEditFromView,
    handleViewPurchaseOrder,
    isEditPurchaseOrder: isEditPurchaseOrderRef.current,
    handlePurchaseOrderAdded,
    handleDelete,
    currencyList,
    commonARIALabel,
    showReOpenPoModal,
    setShowReOpenPoModal,
  };
};
